import React, { useState, useEffect, lazy, Suspense } from "react";
import {
  Routes,
  Route,
  Navigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import axios from "axios";
import Header from "./components/Header/Header";
import Footer from "./components/Footer/Footer";
import LoadingSpinner from "./components/LoadingSpinner/LoadingSpinner";
import CustomCursor from "./components/CustomCursor/CustomCursor";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";
import PrivacyPolicy from "./pages/Legal/PrivacyPolicy";
import TermsOfService from "./pages/Legal/TermsOfService";
import RepliAssistantFloating from "./components/RepliAssistantFloating/RepliAssistantFloating";
import RepliInsightsAssistant from "./components/RepliInsightsAssistant/RepliInsightsAssistant";
import RepliPostSocialAssistant from "./components/RepliPostSocialAssistant/RepliPostSocialAssistant";

// Lazy loading components for performance optimization
const HomePage = lazy(() => import("./pages/HomePage/HomePage"));
const RepliKersPage = lazy(() => import("./pages/RepliKersPage/RepliKersPage"));
const ChatPage = lazy(() => import("./pages/ChatPage/ChatPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage/ProfilePage"));
const LoginPage = lazy(() => import("./pages/LoginPage/LoginPage"));
const BlogPage = lazy(() => import("./pages/BlogPage/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage/BlogPostPage"));
const EditBlogPage = lazy(() => import("./pages/EditBlogPage/EditBlogPage"));
const AboutPage = lazy(() => import("./pages/AboutPage/AboutPage"));
const SocialPage = lazy(() => import("./pages/SocialPage/SocialPage"));
const SocialProfilePage = lazy(() => import("./pages/SocialPage/SocialProfilePage"));
const NotificationsPage = lazy(() => import("./pages/SocialPage/NotificationsPage"));
const PostDetailPage = lazy(() => import("./pages/SocialPage/PostDetailPage"));
const SocialChatPage = lazy(() => import("./pages/SocialPage/ChatPage"));
const ExplorePage = lazy(() => import("./pages/SocialPage/ExplorePage"));
const NewsPage = lazy(() => import("./pages/NewsPage/NewsPage"));
const ArticlePage = lazy(() => import("./pages/ArticlePage/ArticlePage"));
const CreateArticlePage = lazy(() => import("./pages/CreateArticlePage/CreateArticlePage"));
const EditArticlePage = lazy(() => import("./pages/EditArticlePage/EditArticlePage"));
const MessagingInterface = lazy(() => import("./components/MessagingInterface/MessagingInterface"));

// RepliKers App
const App = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const checkSession = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || ""
        }/api/auth/session`,
        {
          withCredentials: true,
        },
      );
      setUser((prevUser) => {
        // Evitar cambiar la referencia si el usuario es estructuralmente igual
        if (prevUser && JSON.stringify(prevUser) === JSON.stringify(response.data.user)) {
          return prevUser;
        }
        return response.data.user;
      });
    } catch (error) {
      setUser(null);
      console.log("No hay sesión activa.");
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL || ""
        }/api/auth/logout`,
        {},
        {
          withCredentials: true,
        },
      );
      setUser(null);
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    }
  };

  // --- MANEJO DE TEMAS ---
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme") || "dark";
    document.body.setAttribute("data-theme", savedTheme);
  }, []);
  // -------------------------------

  useEffect(() => {
    checkSession();
  }, [searchParams]);

  // Sincronización silenciosa cuando el usuario vuelve a la pestaña
  useEffect(() => {
    const handleFocus = () => {
      if (user) {
        // SEGURIDAD: No sincronizar si el usuario está escribiendo para no borrar sus cambios locales
        const activeEl = document.activeElement;
        const isTyping = activeEl && (
          activeEl.tagName === 'INPUT' ||
          activeEl.tagName === 'TEXTAREA' ||
          activeEl.isContentEditable ||
          activeEl.classList.contains('inline-edit-input')
        );

        // También verificamos si hay algún input de edición de perfil visible por clase
        const isEditingProfile = document.querySelector('.inline-edit-input');

        if (!isTyping && !isEditingProfile) {
          checkSession();
        }
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [user]);

  if (loading) {
    return <LoadingSpinner message="Entrando a RepliKers..." />;
  }

  return (
    <div className="App">
      <CustomCursor />
      {user && (
        <Header
          user={user}
          logout={logout}
          showBackButton={location.pathname !== "/"}
          menuOpen={menuOpen}
          setMenuOpen={setMenuOpen}
        />
      )}
      <ScrollToTop />
      <Suspense fallback={<LoadingSpinner message="Cargando..." />}>
        <Routes>
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          {!user ? (
            <>
              <Route path="/login" element={<LoginPage />} />
              {/* Guardamos la ubicación actual (location) para volver a ella tras el login */}
              <Route path="*" element={<Navigate to="/login" state={{ from: location }} replace />} />
            </>
          ) : (
            <>
              <Route path="/" element={<HomePage user={user} />} />
              <Route path="/replikers" element={<RepliKersPage />} />
              <Route
                path="/chat/:replikerId"
                element={<ChatPage user={user} menuOpen={menuOpen} />}
              />
              <Route
                path="/profile"
                element={<ProfilePage user={user} setUser={setUser} />}
              />
              <Route
                path="/account"
                element={<ProfilePage user={user} setUser={setUser} />}
              />
              <Route path="/blog" element={<BlogPage user={user} />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/edit-blog/:slug" element={<EditBlogPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/news" element={<NewsPage user={user} />} />
              <Route path="/news/create" element={<CreateArticlePage user={user} />} />
              <Route path="/news/edit/:id" element={<EditArticlePage user={user} />} />
              <Route path="/news/:slug" element={<ArticlePage user={user} />} />
              <Route path="/social" element={<SocialPage user={user} logout={logout} />} />
              <Route path="/social/profile/:userId" element={<SocialProfilePage user={user} logout={logout} />} />
              <Route path="/social/post/:id" element={<PostDetailPage user={user} logout={logout} />} />
              <Route path="/social/notifications" element={<NotificationsPage user={user} logout={logout} />} />
              <Route path="/social/messages" element={<SocialChatPage user={user} logout={logout} />} />
              <Route path="/social/messages/:userId" element={<SocialChatPage user={user} logout={logout} />} />
              <Route path="/explore" element={<ExplorePage user={user} logout={logout} />} />
              <Route
                path="/messaging"
                element={<MessagingInterface user={user} />}
              />
              <Route path="*" element={<Navigate to="/" />} />
            </>
          )}
        </Routes>
      </Suspense>
      {!location.pathname.startsWith("/chat") && <Footer />}
      {user && !location.pathname.startsWith("/chat") && (
        <>
          <RepliAssistantFloating user={user} />
          <RepliInsightsAssistant user={user} />
          <RepliPostSocialAssistant user={user} />
        </>
      )}
    </div>
  );
};

export default App;
